import type { Transport } from './Transport';

/**
 * Metronome — schedules a click on every integer beat of the transport.
 * Strong click on bar 1, medium on group starts, weak elsewhere
 * (matches Transport.beatStrengthAt).
 *
 * Self-contained: the click is synthesised by a short oscillator burst
 * with an exponential amp envelope. Routes into the supplied `out`
 * node (the engine's pre-limiter bus) so it shares the master chain.
 */
export type BeatStrength = 'strong' | 'medium' | 'weak';
export type OnBeatCb = (beat: number, isDownbeat: boolean, strength: BeatStrength) => void;

export class Metronome {
  enabled = false;
  volume = 0.4;
  lookaheadSec = 0.18;
  tickMs = 40;
  onBeat: OnBeatCb | null = null;

  private _timer: ReturnType<typeof setInterval> | null = null;
  private _nextBeat = 0;
  private _pendingVisual: { beat: number; atTime: number; strength: BeatStrength }[] = [];
  private readonly ctx: AudioContext;
  private readonly transport: Transport;
  private readonly out: AudioNode;

  constructor(ctx: AudioContext, transport: Transport, out: AudioNode) {
    this.ctx = ctx;
    this.transport = transport;
    this.out = out;
  }

  setEnabled(on: boolean): void {
    if (on === this.enabled) return;
    this.enabled = on;
    if (on) {
      // Pick the next integer beat from "now" so we don't fire immediately on toggle.
      const b = Math.floor(this.transport.beatNow() + 0.001) + 1;
      this._nextBeat = b;
      this._pendingVisual = [];
      this._timer = setInterval(() => this._tick(), this.tickMs);
    } else if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
      this._pendingVisual = [];
    }
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
  }

  /** Manually fire a click NOW — used by the UI "BPM tap" / preview. */
  click(strength: BeatStrength = 'strong', at?: number): void {
    this._click(strength, at ?? this.ctx.currentTime);
  }

  private _tick(): void {
    const t = this.transport;
    const ahead = t.audioTime() + this.lookaheadSec;
    // Schedule every beat boundary inside the lookahead window.
    while (true) {
      const at = t.timeAt(this._nextBeat);
      if (at > ahead) break;
      const strength = t.beatStrengthAt(this._nextBeat);
      this._click(strength, at);
      this._pendingVisual.push({ beat: this._nextBeat, atTime: at, strength });
      this._nextBeat += 1;
    }
    // Fire onBeat callbacks for any visual-beats whose audio time has passed.
    const now = t.audioTime();
    while (this._pendingVisual.length && this._pendingVisual[0]!.atTime <= now) {
      const ev = this._pendingVisual.shift()!;
      if (this.onBeat) {
        try {
          this.onBeat(ev.beat, ev.strength === 'strong', ev.strength);
        } catch {
          /* swallow */
        }
      }
    }
  }

  /** Synthesise one tick at `at`. Strong = higher pitched + louder click,
   *  medium = mid, weak = soft low click. */
  private _click(strength: BeatStrength, at: number): void {
    if (this.volume <= 0.001) return;
    const ctx = this.ctx;
    // Per-strength tone + amplitude (kept terse — clicks are sub-50 ms).
    const freq = strength === 'strong' ? 1600 : strength === 'medium' ? 1200 : 900;
    const peak = (strength === 'strong' ? 0.55 : strength === 'medium' ? 0.4 : 0.25) * this.volume;
    const decay = strength === 'strong' ? 0.06 : 0.045;

    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 600;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, at);
    amp.gain.exponentialRampToValueAtTime(peak, at + 0.002);
    amp.gain.exponentialRampToValueAtTime(0.0001, at + decay);
    osc.connect(hp);
    hp.connect(amp);
    amp.connect(this.out);
    osc.start(at);
    osc.stop(at + decay + 0.02);
    osc.onended = () => {
      try {
        osc.disconnect();
        hp.disconnect();
        amp.disconnect();
      } catch {
        /* already disconnected */
      }
    };
  }
}
